import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  Filter,
  ChevronRight,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/App";

export default function Protocols() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [protocols, setProtocols] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [pillarFilter, setPillarFilter] = useState(searchParams.get("pillar") || "all");
  const [urgencyFilter, setUrgencyFilter] = useState(searchParams.get("urgency") || "all");
  const [settingFilter, setSettingFilter] = useState(searchParams.get("setting") || "all");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [protocolsRes, pillarsRes] = await Promise.all([
          api.get("/protocols"),
          api.get("/pillars"),
        ]);
        setProtocols(protocolsRes.data);
        setPillars(pillarsRes.data);
      } catch (error) {
        // Error fetching protocols data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter protocols
  const filteredProtocols = protocols.filter((protocol) => {
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        protocol.title.toLowerCase().includes(query) ||
        protocol.summary.toLowerCase().includes(query) ||
        protocol.situation_tags?.some(tag => tag.toLowerCase().includes(query));
      if (!matchesSearch) return false;
    }
    
    // Pillar
    if (pillarFilter && pillarFilter !== "all") {
      if (protocol.pillar_id !== pillarFilter) return false;
    }
    
    // Urgency
    if (urgencyFilter && urgencyFilter !== "all") {
      if (protocol.urgency !== urgencyFilter) return false;
    }
    
    // Setting
    if (settingFilter && settingFilter !== "all") {
      if (!protocol.applicable_settings?.includes(settingFilter) && 
          !protocol.applicable_settings?.includes("all")) {
        return false;
      }
    }
    
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setPillarFilter("all");
    setUrgencyFilter("all");
    setSettingFilter("all");
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || pillarFilter !== "all" || urgencyFilter !== "all" || settingFilter !== "all";

  const getPillarName = (pillarId) => {
    const pillar = pillars.find(p => p.id === pillarId);
    return pillar?.title || "Unknown";
  };

  return (
    <div data-testid="protocols-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="label-uppercase text-orange-400 mb-4">Protocol Library</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="protocols-title">
              Find the Right Protocol
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mb-8">
              Searchable library of step-by-step protocols for common PPS situations. 
              Each protocol includes roles, timelines, documentation requirements, and downloadable templates.
            </p>

            {/* Search */}
            <div className="flex gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search protocols..."
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="protocol-search-input"
                />
              </div>
              <Button
                variant="outline"
                className="btn-secondary border-white text-white hover:bg-white hover:text-slate-900"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      {showFilters && (
        <section className="bg-slate-50 border-b border-slate-200 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-4 items-end" data-testid="filters-section">
              <div className="w-48">
                <label className="form-label">Pillar</label>
                <Select value={pillarFilter} onValueChange={setPillarFilter}>
                  <SelectTrigger data-testid="pillar-filter">
                    <SelectValue placeholder="All Pillars" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Pillars</SelectItem>
                    {pillars.map((pillar) => (
                      <SelectItem key={pillar.id} value={pillar.id}>
                        {pillar.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-40">
                <label className="form-label">Urgency</label>
                <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                  <SelectTrigger data-testid="urgency-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="crisis">Crisis</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="routine">Routine</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-40">
                <label className="form-label">Setting</label>
                <Select value={settingFilter} onValueChange={setSettingFilter}>
                  <SelectTrigger data-testid="setting-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Settings</SelectItem>
                    <SelectItem value="elementary">Elementary</SelectItem>
                    <SelectItem value="middle">Middle</SelectItem>
                    <SelectItem value="high">High School</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-slate-600"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Results */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-slate-600" data-testid="results-count">
              Showing <span className="font-medium">{filteredProtocols.length}</span> protocols
            </p>
          </div>

          {/* Protocol List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredProtocols.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-sm">
              <p className="text-slate-600 mb-4">No protocols found matching your criteria.</p>
              <Button onClick={clearFilters} data-testid="clear-search-btn">Clear Search</Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="protocols-list">
              {filteredProtocols.map((protocol, index) => (
                <motion.div
                  key={protocol.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/protocols/${protocol.slug}`}
                    className="protocol-card block bg-white border border-slate-200 p-6 hover:border-slate-300"
                    data-testid={`protocol-item-${protocol.slug}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className={`badge-urgency-${protocol.urgency} px-3 py-1 rounded-full text-xs font-mono uppercase`}>
                            {protocol.urgency}
                          </span>
                          <span className="text-xs text-slate-500 font-mono">
                            {protocol.applicable_settings?.join(" / ")}
                          </span>
                          <span className="text-xs text-slate-400">•</span>
                          <span className="text-xs text-slate-500">
                            {getPillarName(protocol.pillar_id)}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                          {protocol.title}
                        </h3>
                        <p className="text-slate-600 mb-4">
                          {protocol.summary}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {protocol.situation_tags?.slice(0, 4).map((tag, i) => (
                            <span 
                              key={i}
                              className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-slate-400 hidden lg:block flex-shrink-0 mt-2" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
